/**
 * Order Monitor (spec Part 1/2/3) — every order TekInvest has ever
 * submitted, across every strategy and broker, with the full set of
 * display fields Part 1 lists. Read-only itself; [Modify] opens
 * `ModifyOrderDialog`, the only control on this page that can change a
 * real broker order.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RefreshIcon from "@mui/icons-material/Refresh";
import positionService from "@/services/positionService";
import type { OrderMonitorRead } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { BROKER_DISPLAY_LABELS, fmtCurrency } from "@/pages/strategy-adjustment/constants";
import { ORDER_STATE_COLORS, ORDER_STATE_LABELS } from "@/pages/execution/constants";
import { ORDER_STATE_ICONS } from "./constants";
import ModifyOrderDialog from "./ModifyOrderDialog";

/** `order.last_updated` was rendering with `toLocaleTimeString()` alone --
 * time only, no date -- so two fills on the same symbol a day apart (or an
 * order left open overnight) were indistinguishable in this column. Keeps
 * the existing time format unchanged and just prepends the date, same
 * `day/short-month/year` style `StrategiesPage.tsx`'s own `formatDate`
 * already uses elsewhere in this app. */
function formatLastUpdated(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  const datePart = d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  const timePart = d.toLocaleTimeString();
  return `${datePart} ${timePart}`;
}

export function OrdersPage() {
  const [orders, setOrders] = useState<OrderMonitorRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [modifyOrder, setModifyOrder] = useState<OrderMonitorRead | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await positionService.listOrders();
      setOrders(result.orders);
      setLastSynced(new Date());
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <ReceiptLongIcon color="primary" />
        
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Every order TekInvest has submitted, across every strategy and broker.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          {lastSynced && (
            <Typography variant="caption" color="text.secondary">
              Last synchronized: {lastSynced.toLocaleTimeString()}
            </Typography>
          )}
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && orders.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No orders yet — orders you place from Strategy Adjustment will appear here.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Broker</TableCell>
              <TableCell>Strategy</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell>Side</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell>Order Type</TableCell>
              <TableCell align="right">Requested</TableCell>
              <TableCell align="right">Avg Executed</TableCell>
              <TableCell align="right">Filled/Remaining</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.order_id}>
                <TableCell>
                  <Chip
                    size="small"
                    label={order.source === "ALGO" ? "Algo" : "Manual"}
                    color={order.source === "ALGO" ? "info" : "default"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{BROKER_DISPLAY_LABELS[order.broker]}</TableCell>
                <TableCell>{order.strategy_key}</TableCell>
                <TableCell>
                  {order.underlying} {String(order.strike)} {order.option_type}
                </TableCell>
                <TableCell>{order.side}</TableCell>
                <TableCell align="right">{order.quantity}</TableCell>
                <TableCell>{order.order_type}</TableCell>
                <TableCell align="right">{fmtCurrency(order.requested_price)}</TableCell>
                <TableCell align="right">{fmtCurrency(order.average_executed_price)}</TableCell>
                <TableCell align="right">
                  {order.filled_quantity}/{order.remaining_quantity}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={`${ORDER_STATE_ICONS[order.status]} ${ORDER_STATE_LABELS[order.status]}`}
                    color={ORDER_STATE_COLORS[order.status]}
                  />
                </TableCell>
                <TableCell>{formatLastUpdated(order.last_updated)}</TableCell>
                <TableCell align="right">
                  {order.modifiable && (
                    <Button size="small" onClick={() => setModifyOrder(order)}>
                      Modify
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ModifyOrderDialog order={modifyOrder} open={Boolean(modifyOrder)} onClose={() => setModifyOrder(null)} onModified={load} />
    </Container>
  );
}

export default OrdersPage;
